import React, { useState } from 'react';
import axios from 'axios';
import { MapPin, Mail, Phone, Send, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { CONTACT } from '../data/mock';
import { toast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Please fill in all fields', description: 'Name, email and message are required.' });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast({
        title: 'Message sent!',
        description: `Thanks ${form.name.split(' ')[0]}, we will be in touch soon.`,
      });
      setForm({ name: '', email: '', message: '' });
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Something went wrong';
      toast({ title: 'Could not send message', description: String(msg).slice(0, 200) });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-[#FFF7F1] to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12">
          <div className="space-y-6">
            <div className="inline-block px-4 py-1.5 bg-[#F55B26]/10 text-[#F55B26] font-semibold text-xs uppercase tracking-widest rounded-full">
              Let&apos;s Chat
            </div>
            <h2 className="text-4xl lg:text-5xl font-extrabold text-[#0B3B44] leading-tight">
              Get in <span className="text-[#F55B26]">touch</span> with us.
            </h2>
            <p className="text-lg text-slate-600">
              Have a question about volunteering, donating, or partnering with us? We&apos;d love to hear from you.
            </p>

            <div className="space-y-4 pt-4">
              <a
                href={`mailto:${CONTACT.email}`}
                className="flex items-start gap-4 p-5 bg-white rounded-2xl border border-slate-100 hover:border-[#F55B26]/40 hover:shadow-md transition-all group"
              >
                <div className="w-12 h-12 rounded-xl bg-[#FFEFE2] flex items-center justify-center flex-shrink-0 group-hover:bg-[#F55B26] transition-colors">
                  <Mail className="w-6 h-6 text-[#F55B26] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-widest font-semibold">Email</div>
                  <div className="font-bold text-[#0B3B44]">{CONTACT.email}</div>
                </div>
              </a>

              <a
                href={`tel:${CONTACT.phone.replace(/\s/g, '')}`}
                className="flex items-start gap-4 p-5 bg-white rounded-2xl border border-slate-100 hover:border-[#F55B26]/40 hover:shadow-md transition-all group"
              >
                <div className="w-12 h-12 rounded-xl bg-[#FFEFE2] flex items-center justify-center flex-shrink-0 group-hover:bg-[#F55B26] transition-colors">
                  <Phone className="w-6 h-6 text-[#F55B26] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-widest font-semibold">Phone</div>
                  <div className="font-bold text-[#0B3B44]">{CONTACT.phone}</div>
                </div>
              </a>

              <div className="flex items-start gap-4 p-5 bg-white rounded-2xl border border-slate-100">
                <div className="w-12 h-12 rounded-xl bg-[#FFEFE2] flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-[#F55B26]" />
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-widest font-semibold">Address</div>
                  <div className="font-bold text-[#0B3B44]">{CONTACT.address}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-8 lg:p-10 shadow-xl border border-slate-100">
            <h3 className="text-2xl font-extrabold text-[#0B3B44] mb-2">Send us a message</h3>
            <p className="text-sm text-slate-500 mb-6">We usually reply within 24 hours.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-widest mb-1.5 block">Your name</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jane Doe"
                  className="h-12 bg-[#FFF7F1] border-2 border-transparent focus-visible:border-[#F55B26] focus-visible:ring-0 rounded-xl"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-widest mb-1.5 block">Email address</label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="jane@example.com"
                  className="h-12 bg-[#FFF7F1] border-2 border-transparent focus-visible:border-[#F55B26] focus-visible:ring-0 rounded-xl"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-widest mb-1.5 block">How can we help?</label>
                <Textarea
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Tell us a bit about how you'd like to get involved..."
                  className="bg-[#FFF7F1] border-2 border-transparent focus-visible:border-[#F55B26] focus-visible:ring-0 rounded-xl resize-none"
                />
              </div>
              <Button
                type="submit"
                disabled={sending}
                className="w-full py-6 bg-[#F55B26] hover:bg-[#E14D1F] text-white font-bold rounded-full shadow-md hover:shadow-lg transition-all disabled:opacity-70"
              >
                {sending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" />Send message</>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
